import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Collapse, IconButton, Link,
  Paper, Rating, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedIcon from '@mui/icons-material/VerifiedOutlined';
import CheckIcon from '@mui/icons-material/Check';

import { api, ApiError } from '../api/client';
import type { Category, PlanRequest, Poi, StopRecommendation } from '../api/types';

/**
 * What the corridor actually offers, ranked, before anything is planned.
 *
 * The planner already ranks candidates and hands them to the optimiser, which
 * keeps what fits and discards the rest silently. A traveller therefore never
 * sees what was on offer — only what survived. This exposes the ranking
 * itself, so a day can be chosen rather than only accepted.
 *
 * Deliberately not a plan: no matrix, no solve, no routing. It is a search,
 * and it answers in the time a search takes.
 */

interface Sets {
  stops: StopRecommendation[];
  lunch: StopRecommendation[];
  coffee: StopRecommendation[];
}

/**
 * What the map should draw for the list, and which row is under the cursor.
 *
 * Held with the rest of the recommendation state rather than passed around
 * separately, because all three lists feed one map and the last one hovered
 * wins — which is the behaviour anybody would expect and the hardest one to
 * get by wiring each list to the map independently.
 */
export interface MapPreview {
  places: { poiId: string; name: string; lat: number; lng: number }[];
  hoveredPoiId: string | null;
  /*
   * What these markers are being offered FOR.
   *
   * The map draws all three lists the same way, but clicking a marker has to
   * open the right thing: a place to visit is added to the round, while a
   * place offered for lunch fills the lunch slot. Carried with the markers
   * because the map has no other way to tell them apart — and because the
   * last list published is the one on screen, so one field is enough.
   */
  role: 'visit' | 'lunch' | 'coffee';
}

export interface RecommendationState {
  data: Sets | null;
  loading: boolean;
  error: string | null;
  /** Drawn on the map. Empty when the traveller has switched that off. */
  preview: MapPreview;
  setPreview: (next: MapPreview) => void;
  /** What the traveller said they were interested in. Empty until they say. */
  interests: string[];
  setInterests: (keys: string[]) => void;
  /**
   * Builds one or more of the lists. Each is its own search on the server and
   * its own batch of provider lookups, so a panel asks for its own list only
   * — pressing "show me places" should not pay to rank lunch and coffee too.
   */
  load: (lists?: readonly ('stops' | 'lunch' | 'coffee')[]) => void;
  /** Which lists have actually been fetched, so each panel knows its own. */
  loaded: Record<'stops' | 'lunch' | 'coffee', boolean>;
  /** Which list is in flight, so only that panel shows a spinner. */
  loadingList: 'stops' | 'lunch' | 'coffee' | null;
  dismissError: () => void;
}

/**
 * Fetched once for the whole form, not once per list.
 *
 * Places, lunch and coffee all come from a single response and are shown in
 * three different parts of the form. Letting each fetch its own would ask the
 * same question three times, and let the three answers drift apart while the
 * traveller is still editing.
 */
export function useRecommendations(draft: PlanRequest | null): RecommendationState {
  const [data, setData] = useState<Sets | null>(null);
  const [interests, setInterests] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState<'stops' | 'lunch' | 'coffee' | null>(null);
  const [loaded, setLoaded] = useState<Record<'stops' | 'lunch' | 'coffee', boolean>>({
    stops: false, lunch: false, coffee: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MapPreview>(
    { places: [], hoveredPoiId: null, role: 'visit' },
  );

  /*
   * Dropped when the request changes in a way that changes the ranking.
   *
   * Keyed on the corridor rather than on the draft object, because most of
   * what the form edits does not affect this at all: choosing a place for
   * lunch, renaming a stay, reordering stops. Watching the whole draft threw
   * the coffee suggestions away the moment lunch was picked from them, which
   * is the one moment they were about to be used.
   *
   * Start, end, mode and time budget DO change it — a list ranked by "how
   * little this takes you off the route" is meaningless against a different
   * route — as does anything that changes which places are eligible.
   */
  const corridor = draft && JSON.stringify([
    draft.cityId, draft.start, draft.end, draft.mode, draft.availableMinutes,
    draft.routeStyleKeys ?? [], draft.excludePoiIds ?? [],
    draft.avoid.poiIds, draft.avoid.categoryKeys ?? [], draft.avoid.distanceM ?? null,
    // Only whether a break was asked for, not where it landed.
    Boolean(draft.lunch), Boolean(draft.coffee),
  ]);
  useEffect(() => {
    setData(null);
    setLoaded({ stops: false, lunch: false, coffee: false });
    setError(null);
    // Markers for a corridor that no longer exists would be worse than none.
    setPreview({ places: [], hoveredPoiId: null, role: 'visit' });
  }, [corridor]);

  const load = (lists: readonly ('stops' | 'lunch' | 'coffee')[] = ['stops']) => {
    if (!draft) return;
    setLoading(true);
    setLoadingList(lists.length === 1 ? lists[0]! : null);
    setError(null);
    void (async () => {
      try {
        const result = await api.recommendations({
          ...draft,
          /*
           * Sent here and only here.
           *
           * The same field on a plan decides which stops the optimiser gets
           * to choose from, so filling it in from this picker would quietly
           * re-plan the day from a control that presents itself as a way to
           * browse.
           */
          interestCategoryKeys: interests,
        }, lists);

        /*
         * Merged, not replaced. The three panels ask separately, so a lunch
         * response carries empty arrays for the other two — writing it over
         * the whole object would blank a places list somebody was reading.
         */
        setData((current) => ({
          stops: lists.includes('stops') ? result.stops : current?.stops ?? [],
          lunch: lists.includes('lunch') ? result.lunch : current?.lunch ?? [],
          coffee: lists.includes('coffee') ? result.coffee : current?.coffee ?? [],
        }));
        setLoaded((current) => ({
          stops: current.stops || lists.includes('stops'),
          lunch: current.lunch || lists.includes('lunch'),
          coffee: current.coffee || lists.includes('coffee'),
        }));
      } catch (err) {
        setError((err as ApiError).message);
      } finally {
        setLoading(false);
        setLoadingList(null);
      }
    })();
  };

  return {
    data, loading, loadingList, loaded, error, interests, load, preview, setPreview,
    // Changing what you like invalidates what you were shown.
    setInterests: (keys) => { setInterests(keys); setData(null); },
    dismissError: () => setError(null),
  };
}

/**
 * A recommendation carries only what a list needs; adding one wants a whole
 * place. Rebuilt here rather than fetched again — every field the callers use
 * is already present.
 */
function asPoi(r: StopRecommendation, cityId: string): Poi {
  return {
    id: r.poiId,
    cityId,
    categoryId: '',
    categoryKey: r.categoryKey,
    categoryLabel: r.categoryLabel,
    name: r.name,
    lat: r.lat,
    lng: r.lng,
    address: r.address,
    description: null,
    rating: r.rating,
    openingHours: null,
    dwellMinutes: r.dwellMinutes,
    source: 'osm',
    phone: null,
    website: null,
  };
}

function Row({
  item, chosen, addLabel, chosenLabel, onAdd, onOpen, onHover,
}: {
  item: StopRecommendation;
  chosen: boolean;
  addLabel: string;
  chosenLabel: string;
  onAdd: () => void;
  /** Opens the full record — hours, contact details, map link. */
  onOpen: () => void;
  /** Picks this one out on the map, and clears it on the way out. */
  onHover: (over: boolean) => void;
}) {
  return (
    <Stack
      direction="row"
      spacing={1}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      sx={{
        alignItems: 'center',
        py: 0.75,
        px: 0.5,
        mx: -0.5,
        borderRadius: 1,
        opacity: chosen ? 0.65 : 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/*
          The name opens the place rather than only labelling it. A row this
          terse — category, stay, rating — is not enough to decide on, and the
          details dialog already holds everything that is known.
        */}
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={onOpen}
          sx={{
            display: 'block', maxWidth: '100%', textAlign: 'left',
            fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.43,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {item.name}
        </Link>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {item.categoryLabel} · {item.dwellMinutes} min
            {/*
              What it costs to go, on top of what it costs to stay. Routed by
              our own engine over real roads — the ranking uses straight-line
              distance, which is wrong exactly where it matters most, on the
              far side of a river with one bridge.
            */}
            {item.detourMinutes !== null && (
              item.detourMinutes === 0
                ? ' · on the way'
                : ` · +${item.detourMinutes} min detour`
            )}
          </Typography>
          {/*
            Shown only where the enrichment cache has reached the place. A
            blank rating would read as "nobody likes it" rather than "nobody
            has told us".
          */}
          {item.rating !== null && (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Rating value={item.rating} precision={0.1} size="small" readOnly />
              <Typography variant="caption" color="text.secondary">
                {item.rating.toFixed(1)}
                {item.userRatingCount ? ` (${item.userRatingCount.toLocaleString('en-US')})` : ''}
              </Typography>
            </Stack>
          )}
          {/*
            Shown only where it is true, and only where there is no rating to
            say it already. Marking every confirmed place would put a badge on
            almost the whole list; marking the unconfirmed ones would read as
            an accusation against places that are very probably fine.
          */}
          {item.verified && item.rating === null && (
            <Tooltip title="Confirmed against Google Places" arrow>
              <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
                <VerifiedIcon sx={{ fontSize: 13, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">verified</Typography>
              </Stack>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Button
        size="small"
        variant={chosen ? 'text' : 'outlined'}
        disabled={chosen}
        startIcon={chosen ? <CheckIcon fontSize="small" /> : <AddIcon fontSize="small" />}
        onClick={onAdd}
        sx={{ flexShrink: 0 }}
      >
        {chosen ? chosenLabel : addLabel}
      </Button>
    </Stack>
  );
}

/**
 * A ranked list that starts short and grows.
 *
 * Every list here is the same shape and the same problem: the ranking runs
 * deeper than anyone wants to read at once, but the tail is exactly where
 * somebody looking for something particular will find it. Opening with a
 * handful and saying plainly how many more there are beats both a wall of
 * results and a list that silently stops.
 */
function Ranked({
  items, initial, chosenFor, addLabel, chosenLabel, onAdd, onOpen, onShownChange, onHover,
}: {
  items: StopRecommendation[];
  initial: number;
  chosenFor: (r: StopRecommendation) => boolean;
  addLabel: string;
  chosenLabel: string;
  onAdd: (r: StopRecommendation) => void;
  onOpen: (r: StopRecommendation) => void;
  /** What is currently on screen, so the map can draw exactly that. */
  onShownChange: (visible: StopRecommendation[]) => void;
  onHover: (r: StopRecommendation | null) => void;
}) {
  const [shown, setShown] = useState(initial);
  const remaining = items.length - shown;
  const visible = items.slice(0, shown);

  /*
   * The map follows what the list shows, not what it holds.
   *
   * Expanding the list adds markers and collapsing it removes them, so the two
   * never disagree about how much is being offered — twenty-four rings under a
   * list of eight would be unreadable and, worse, wrong.
   */
  useEffect(() => {
    onShownChange(visible);
    // Keyed on the ids rather than the array, which is new on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.map((r) => r.poiId).join(',')]);

  return (
    <Box>
      <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
        {visible.map((r) => (
          <Row
            key={r.poiId}
            item={r}
            chosen={chosenFor(r)}
            addLabel={addLabel}
            chosenLabel={chosenLabel}
            onAdd={() => onAdd(r)}
            onOpen={() => onOpen(r)}
            onHover={(over) => onHover(over ? r : null)}
          />
        ))}
      </Stack>

      {remaining > 0 && (
        <Button
          size="small"
          fullWidth
          endIcon={<ExpandMoreIcon fontSize="small" />}
          onClick={() => setShown(items.length)}
          sx={{ mt: 0.5 }}
        >
          Show {remaining} more
        </Button>
      )}
      {/* Collapsing again matters on the break lists, which sit above the
          plan button and would otherwise push it off the screen. */}
      {remaining === 0 && shown > initial && (
        <Button size="small" fullWidth onClick={() => setShown(initial)} sx={{ mt: 0.5 }}>
          Show fewer
        </Button>
      )}
    </Box>
  );
}

/** Places to visit, ranked. Sits beside the picker it feeds. */
export function StopRecommendations({
  state, cityId, categories, chosenIds, onAddStop, onShowDetails, ready = true,
}: {
  state: RecommendationState;
  cityId: string;
  /**
   * Whether there is a journey to rank against yet. False keeps the panel on
   * screen but unable to ask — see the button below.
   */
  ready?: boolean;
  /** Everything visitable in this city, for the interest picker. */
  categories: Category[];
  /** Places already requested, so they are shown as added rather than offered. */
  chosenIds: string[];
  onAddStop: (poi: Poi) => void;
  onShowDetails: (poiId: string, role?: 'visit' | 'lunch' | 'coffee') => void;
}) {
  const { data, loaded, loadingList, error, interests, setInterests, load, dismissError,
    preview, setPreview } = state;
  const loading = loadingList === 'stops';

  /*
   * On by default, and remembered for the session.
   *
   * A ranked list with no positions is half an answer — "how far off my route
   * is this?" is one of the two questions the ranking is built on, and it is
   * the one a list cannot show.
   */
  const [onMap, setOnMap] = useState(true);
  /*
   * Shut until asked for.
   *
   * The panel is three controls and a list, and it sits in the middle of the
   * section where stops are chosen — open by default it pushed the time
   * budget and everything under it off the screen, for a list most rounds
   * never ask for. The heading stays visible, so it can still be found.
   */
  const [open, setOpen] = useState(false);
  /*
   * What the list is currently showing, kept here rather than derived when
   * needed. The switch has to be able to redraw markers for rows that have not
   * changed — publishing only from the list's own change meant turning the map
   * back on left it empty until something else moved.
   */
  const [visible, setVisible] = useState<StopRecommendation[]>([]);

  useEffect(() => {
    setPreview({
      places: onMap
        ? visible.map((r) => ({ poiId: r.poiId, name: r.name, lat: r.lat, lng: r.lng }))
        : [],
      hoveredPoiId: null,
      role: 'visit',
    });
  }, [visible, onMap, setPreview]);


  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography
          component="button"
          type="button"
          onClick={() => setOpen((was) => !was)}
          aria-expanded={open}
          variant="caption"
          sx={{
            fontWeight: 700, letterSpacing: 0.4, flex: 1, textAlign: 'left',
            display: 'flex', alignItems: 'center', gap: 0.5,
            // Only the browser's own button chrome is reset; the caption
            // variant supplies the type, and `font: inherit` would undo it.
            background: 'none', border: 0, p: 0, cursor: 'pointer', color: 'inherit',
          }}
        >
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 150ms',
            }}
          />
          RECOMMENDED ALONG THE WAY
        </Typography>
        {/* Only while it is open: acting on a list nobody can see is a
            button that appears to do nothing. */}
        {open && loaded.stops && (
          <>
            <Tooltip title={onMap ? 'Hide these from the map' : 'Show these on the map'} arrow>
              <IconButton
                size="small"
                color={onMap ? 'primary' : 'default'}
                onClick={() => setOnMap((on) => !on)}
                aria-label={onMap ? 'Hide suggestions from the map' : 'Show suggestions on the map'}
              >
                <MapOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" onClick={() => load(['stops'])} disabled={loading}>
              Refresh
            </Button>
          </>
        )}
      </Stack>

      <Collapse in={open} unmountOnExit>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 1.5 }}>
        Say what you are interested in and we will rank what sits between your
        start and destination — by how well known it is, how well rated, and
        how little it takes you off the route.
      </Typography>

      {/*
        Asked before anything is suggested, rather than guessed at. Ranking
        the whole catalogue puts a fast-food outlet beside a national museum
        and calls both a recommendation.
      */}
      <Autocomplete
        multiple
        size="small"
        options={categories.map((c) => c.key)}
        value={interests}
        onChange={(_, next) => setInterests(next)}
        getOptionLabel={(key) => categories.find((c) => c.key === key)?.label ?? key}
        renderInput={(params) => (
          <TextField
            {...params}
            label="What are you interested in?"
            placeholder={interests.length === 0 ? 'Museums, parks, landmarks…' : ''}
          />
        )}
        sx={{ mb: 1.5 }}
      />

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }} onClose={dismissError}>
          {error}
        </Alert>
      )}

      {/*
        The button says what is missing, rather than the panel vanishing.

        This whole block used to render only once both ends were set — sound
        in itself, since "on the way" means nothing without a way, but it put
        the panel where somebody opening this section to look for suggestions
        found an empty box and no reason for it. Present and unable to ask is
        a state a person can act on; absent is not.
      */}
      {!loaded.stops && (
        <Button
          size="small"
          variant="outlined"
          disabled={loading || !ready || interests.length === 0}
          onClick={() => load(['stops'])}
          startIcon={loading ? <CircularProgress size={14} /> : undefined}
        >
          {loading ? 'Looking…'
            : !ready ? 'Set a start and destination first'
              : interests.length === 0 ? 'Pick an interest first' : 'Show recommendations'}
        </Button>
      )}

      {loaded.stops && data && (
        <Stack spacing={1}>
          {data.stops.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Nothing of that kind sits along this corridor. Try another
              interest, or a longer day.
            </Typography>
          ) : (
            <>
              <Ranked
                items={data.stops}
                initial={8}
                chosenFor={(r) => chosenIds.includes(r.poiId)}
                addLabel="Add"
                chosenLabel="Added"
                onAdd={(r) => onAddStop(asPoi(r, cityId))}
                onOpen={(r) => onShowDetails(r.poiId)}
                onShownChange={setVisible}
                onHover={(r) => setPreview({ ...preview, hoveredPoiId: r?.poiId ?? null })}
              />
              <Chip
                size="small"
                variant="outlined"
                label="Added places are kept in the route if at all possible"
                sx={{ alignSelf: 'flex-start' }}
              />
            </>
          )}
        </Stack>
      )}
      </Collapse>
    </Paper>
  );
}

/**
 * The same ranking, narrowed to one break, shown under that break's own
 * picker.
 *
 * Deliberately not bundled with the places above: choosing where to have
 * lunch is a decision made in the breaks section, and a list of cafés filed
 * under "places to visit" is a list nobody reads.
 *
 * The interest picker does not gate these. The interest is the break itself,
 * and it was stated by switching the break on.
 */
export function BreakRecommendations({
  state, role, cityId, chosen, onChoose, onShowDetails,
  ready = true, notReadyLabel = 'Set a start and destination first',
  canShowOnMap = false,
}: {
  state: RecommendationState;
  role: 'lunch' | 'coffee';
  cityId: string;
  /**
   * Whether the page this sits on actually draws the preview.
   *
   * The one-day planner does; the multi-day form has no map wired to it, and
   * a button there would publish markers nothing reads — which looks like a
   * broken switch rather than a missing feature.
   */
  canShowOnMap?: boolean;
  /**
   * Whether there is a journey to rank against yet. False keeps the button on
   * screen and says what is missing — `load` quietly does nothing without a
   * draft, so an enabled button would appear broken.
   */
  ready?: boolean;
  /** What is missing, said on the button. Differs by where this sits. */
  notReadyLabel?: string;
  /** The place already picked for this break, if any. */
  chosen: Poi | null;
  onChoose: (poi: Poi) => void;
  onShowDetails: (poiId: string, role?: 'visit' | 'lunch' | 'coffee') => void;
}) {
  const { data, loaded, loadingList, load, preview, setPreview } = state;
  const items = data?.[role] ?? [];
  const loading = loadingList === role;

  /*
   * Whether these are drawn on the map as a set, rather than one at a time
   * under the cursor.
   *
   * Off until asked for. A hovered marker answers "where is this one"; the
   * whole set answers "what is around there", which is the question somebody
   * deciding where to eat is actually asking — but it is also a screen full
   * of rings, so it is offered rather than imposed.
   */
  const [onMap, setOnMap] = useState(false);
  /*
   * What the list is showing, so the markers follow it: expanding the list
   * adds rings and collapsing it takes them away.
   */
  const [visible, setVisible] = useState<StopRecommendation[]>([]);

  const marker = (r: StopRecommendation) => (
    { poiId: r.poiId, name: r.name, lat: r.lat, lng: r.lng }
  );

  /*
   * Whether the markers on the map are ours, right now.
   *
   * There is one preview and the last list to publish owns it, so `onMap`
   * alone is a wish rather than a fact: switch coffee on and lunch's rings
   * are gone, while lunch's button still claims them.
   *
   * Read from the preview instead of tracked. An effect that compared the
   * two and stood itself down fired against its OWN markers — effects see
   * the state of the render they were queued in, so the publish had not
   * landed yet, and the switch turned itself off the moment it was pressed
   * while the rings it had just put up stayed on the map.
   */
  const showingMine = onMap && preview.role === role && preview.places.length > 0;

  /*
   * The markers follow the list while they are ours: expanding it adds rings
   * and collapsing it takes them away. Keyed on the list alone — pressing
   * the switch publishes for itself, below, so that it can also publish when
   * another list has taken the map over and `onMap` has not changed.
   */
  useEffect(() => {
    if (!onMap) return;
    setPreview({ places: visible.map(marker), hoveredPoiId: null, role });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!loaded[role]) {
    return (
      <Button
        size="small"
        disabled={loading || !ready}
        onClick={() => load([role])}
        sx={{ mt: 0.5 }}
      >
        {loading ? 'Looking…' : !ready ? notReadyLabel : 'Suggest somewhere'}
      </Button>
    );
  }

  if (items.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Nothing suitable found along this route. The planner still looks again
        when it builds the day.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, flex: 1 }}>
          SUGGESTIONS
        </Typography>
        {/*
          All of them at once, on the map.

          Hovering a row has always picked one out, which answers "where is
          this one". Deciding where to eat is the other question — what is
          around there, and which of them is on the way — and that is a
          question only the set can answer. Still a switch and still off by
          default, because three lists of markers at once is a map of
          everything.
        */}
        {canShowOnMap && (
          <Tooltip
            title={showingMine ? 'Hide these from the map' : 'Show these on the map'}
            arrow
          >
            <IconButton
              size="small"
              color={showingMine ? 'primary' : 'default'}
              onClick={() => {
                if (showingMine) {
                  setOnMap(false);
                  setPreview({ places: [], hoveredPoiId: null, role });
                  return;
                }
                setOnMap(true);
                setPreview({ places: visible.map(marker), hoveredPoiId: null, role });
              }}
              aria-label={
                showingMine
                  ? `Hide ${role} suggestions from the map`
                  : `Show ${role} suggestions on the map`
              }
            >
              <MapOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Said only while the markers are up, where it is the next thing to do. */}
      {canShowOnMap && showingMine && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Click a ring on the map to read about the place and use it for this break.
        </Typography>
      )}
      <Ranked
        items={items}
        initial={5}
        chosenFor={(r) => chosen?.id === r.poiId}
        addLabel="Use"
        chosenLabel="Chosen"
        onAdd={(r) => onChoose(asPoi(r, cityId))}
        onOpen={(r) => onShowDetails(r.poiId, role)}
        onShownChange={setVisible}
        onHover={(r) => {
          /*
           * With the set drawn, hovering picks one of them out rather than
           * replacing them — publishing a single marker here emptied the map
           * of everything the switch had just put on it.
           */
          if (showingMine) {
            setPreview({
              places: visible.map(marker),
              hoveredPoiId: r?.poiId ?? null,
              role,
            });
            return;
          }
          setPreview(
            r
              ? { places: [marker(r)], hoveredPoiId: r.poiId, role }
              : { places: [], hoveredPoiId: null, role },
          );
        }}
      />
    </Box>
  );
}
